import {DOCUMENT} from '@angular/common';
import {ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, Type, createComponent, inject} from '@angular/core';
import {AnimationModel, AnimationSettingsModel, ResizeDirections} from "@syncfusion/ej2-angular-popups";

export interface DynamicDialogRef<T> {
    componentRef: ComponentRef<T>;
    close: () => void;
}

@Injectable({
    providedIn: 'root'
})
export class DialogService {
    private applicationRef: ApplicationRef = inject(ApplicationRef);
    private environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);
    private document: Document = inject(DOCUMENT);
    private animationSettings: AnimationSettingsModel = {effect: 'Fade', duration: 400, delay: 0};
    private resizeHandleDirection: ResizeDirections[] = ['All'];
    private position: object = {X: 'center', Y: 'center'};
    private tooltipAnimation:  AnimationModel = {open: {effect: 'FadeIn'}, close: {effect: 'FadeOut'}};

    public getAnimationSettings(): AnimationSettingsModel {
        return this.animationSettings;
    }

    public getResizeHandleDirection(): ResizeDirections[] {
        return this.resizeHandleDirection;
    }

    public getPosition(): object {
        return this.position;
    }

    public getTooltipAnimation(): object {
        return this.tooltipAnimation;
    }

    public openComponent<T>(component: Type<T>, inputs: Record<string, unknown> = {}): DynamicDialogRef<T> {
        const hostElement = this.document.createElement('div');
        this.document.body.appendChild(hostElement);

        const componentRef = createComponent(component, {
            environmentInjector: this.environmentInjector,
            hostElement
        });

        Object.entries(inputs).forEach(([key, value]) => {
            componentRef.setInput(key, value);
        });

        this.applicationRef.attachView(componentRef.hostView);
        componentRef.changeDetectorRef.detectChanges();

        let isClosed = false;
        const close = (): void => {
            if (isClosed) {
                return;
            }

            isClosed = true;
            this.applicationRef.detachView(componentRef.hostView);
            componentRef.destroy();
            hostElement.remove();
        };

        componentRef.onDestroy(() => {
            hostElement.remove();
        });

        return {
            componentRef,
            close
        };
    }
}
